import {
    type FragmentType,
    getFragmentData,
    graphql
} from '~/resources/graphql'
import { computed, ref, type Ref, watch } from 'vue'
import type { AttachmentInput } from '~/resources/graphql/graphql'
import type { EnrollmentInputArray } from '~/composables/enrollment-input'
import type { AttachmentRequirement } from '~/components/PurchaseContract/ParticipantDetails/enrollment-constraint'
import { useLayoutMode } from '~/composables/use-layout-mode'

export type ContractAttachmentData = {
    attachmentTypeId: string
    attachmentTypeName: string
    file: File
}

export const ContractConstraintPlanContractFragment = graphql(`
    fragment ContractConstraintPlanContractFragment on PlanContract {
        id
        contractConstraints {
            constraint {
                id
                constraintEnforced
                constraintTranslatedMessage(language: ENGLISH)
            }
            valid
            attachment {
                attachmentType {
                    id
                    name
                }
                attachmentResult {
                    title
                    valid
                    expiresAt
                }
            }
        }
    }
`)

export function useContractConstraint(
    enrollmentInput: Ref<EnrollmentInputArray>,
    contract: Ref<FragmentType<typeof ContractConstraintPlanContractFragment>>
) {
    const { isKiosk } = useLayoutMode()

    const contractData = computed(() =>
        getFragmentData(ContractConstraintPlanContractFragment, contract.value)
    )

    const contractAttachments = ref<ContractAttachmentData[]>([])

    const contractConstraints = computed(() => {
        return contractData.value.contractConstraints
            .map(constraint => {
                // Skip anybody who matches on customer segment
                if (constraint.valid) {
                    return null
                }

                // Skip anybody who doesn't need an attachment
                const constraintAttachment = constraint.attachment
                if (constraintAttachment === null) {
                    return {
                        constraintConfig: {
                            required: isKiosk.value
                                ? false
                                : constraint.constraint.constraintEnforced,
                            message:
                                constraint.constraint
                                    .constraintTranslatedMessage
                        },
                        attachmentRequirement: null
                    }
                }

                let attachmentResult = null
                if (constraintAttachment.attachmentResult === null) {
                    const uploadedAttachment = contractAttachments.value.find(
                        attachment =>
                            attachment.attachmentTypeId ===
                            constraintAttachment.attachmentType.id
                    )

                    if (uploadedAttachment !== undefined) {
                        attachmentResult = {
                            title: uploadedAttachment.file.name,
                            valid: false,
                            expiresAt: null
                        }
                    }
                } else {
                    attachmentResult = {
                        title: constraintAttachment.attachmentResult.title,
                        valid: constraintAttachment.attachmentResult.valid,
                        expiresAt:
                            constraintAttachment.attachmentResult.expiresAt
                    }
                }

                return {
                    constraintConfig: {
                        required: isKiosk.value
                            ? false
                            : constraint.constraint.constraintEnforced,
                        message:
                            constraint.constraint.constraintTranslatedMessage
                    },
                    attachmentRequirement: {
                        attachmentTypeId:
                            constraintAttachment.attachmentType.id,
                        attachmentTypeName:
                            constraintAttachment.attachmentType.name,
                        attachmentResult
                    }
                }
            })
            .filter(constraint => constraint !== null)
    })

    const attachmentsAreInvalid = computed(() => {
        // Check every enrollment slot
        return !contractData.value.contractConstraints.every(constraint => {
            if (constraint.valid) {
                return true
            }

            // Not actually required - just a warning
            if (!constraint.constraint.constraintEnforced) {
                return true
            }

            // Attachment requirement found
            if (constraint.attachment !== null) {
                const result = constraint.attachment
                // Already has one on the server - valid
                if (result.attachmentResult !== null) {
                    return true
                } else {
                    // Try to find in pending uploads
                    const uploadingAttachment = contractAttachments.value.find(
                        attachment =>
                            attachment.attachmentTypeId ===
                            result.attachmentType.id
                    )

                    // Have it locally in browser to upload - return true
                    if (uploadingAttachment !== undefined) {
                        return true
                    } else {
                        // Kiosk mode does not require file uploads
                        if (isKiosk.value) {
                            return true
                        }

                        return false
                    }
                }
            }

            return false
        })
    })

    function getAttachmentInput(): AttachmentInput[] {
        return contractAttachments.value.map(attachment => {
            return {
                enrollmentSlot: null,
                attachmentTypeId: attachment.attachmentTypeId,
                file: attachment.file
            }
        })
    }

    function resetAttachmentUploads(): void {
        contractAttachments.value = []
    }

    // If we uploaded an attachment, but no longer need it, delete it
    watch(
        () => contractData.value.contractConstraints,
        newValue => {
            contractAttachments.value = contractAttachments.value.filter(
                attachment => {
                    return newValue.some(constraint => {
                        return (
                            !constraint.valid &&
                            constraint.attachment !== null &&
                            constraint.attachment.attachmentType.id !==
                                attachment.attachmentTypeId
                        )
                    })
                }
            )
        }
    )

    function handleFileUpload(
        attachmentRequirement: AttachmentRequirement,
        file: File
    ) {
        contractAttachments.value = [
            ...contractAttachments.value,
            {
                attachmentTypeId: attachmentRequirement.attachmentTypeId,
                attachmentTypeName: attachmentRequirement.attachmentTypeName,
                file
            }
        ]
    }

    function handleReplaceFileUpload(
        attachmentRequirement: AttachmentRequirement,
        file: File
    ) {
        contractAttachments.value = [
            ...contractAttachments.value.filter(
                attachment =>
                    !(
                        attachment.attachmentTypeId ===
                        attachmentRequirement.attachmentTypeId
                    )
            ),
            {
                attachmentTypeId: attachmentRequirement.attachmentTypeId,
                attachmentTypeName: attachmentRequirement.attachmentTypeName,
                file
            }
        ]
    }

    return {
        contractAttachments,
        attachmentsAreInvalid,
        getAttachmentInput,
        resetAttachmentUploads,

        contractConstraints: {
            contractConstraints,
            handleFileUpload,
            handleReplaceFileUpload
        }
    }
}

export type ContractConstraints = ReturnType<
    typeof useContractConstraint
>['contractConstraints']
